import {
  Button,
  EmptyState,
  Label,
  Loader,
  Panel,
  SectionHeading,
  Table,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import axios from "axios";
import CenterColumn from "components/CenterColumn";
import { PageHeader } from "components/PageHeader";
import { useEffect, useState } from "react";
import { FaUsers } from "react-icons/fa";
import { Link } from "react-router-dom";

type UserGroup = {
  id: number;
  name: string;
  createdOn: Date;
  membershipLevel: number;
};

const levelNames = ["Invited", "Member", "Admin", "Owner"];

const MyGroups = () => {
  const [user] = useUser();
  const [groups, setGroups] = useState<UserGroup[] | null>(null);

  useEffect(() => {
    axios.get("/api/groups").then(({ data }) => {
      setGroups(data.filter((x) => x.membershipLevel != null));
    });
  }, []);

  const loading = groups == null;

  const acceptInvitation = (x: UserGroup) => {
    axios.put(`/api/groups/${x.id}/memberships/${user.id}/accept`).then(() => {
      setGroups((old) =>
        old.map((o) => ({
          ...o,
          membershipLevel: o.id === x.id ? 1 : o.membershipLevel,
        })),
      );
    });
  };

  const rejectInvitation = (x: UserGroup) => {
    axios.delete(`/api/groups/${x.id}/memberships/${user.id}`);
    setGroups((old) => old.filter((o) => o.id !== x.id));
  };

  const activeGroups = groups?.filter((x) => x.membershipLevel > 0);
  const invitedGroups = groups?.filter((x) => x.membershipLevel === 0);

  return (
    <>
      <CenterColumn>
        <PageHeader
          text="My Groups"
          subtitle="Groups that you currently belong to"
        />
        {loading ? (
          <Loader />
        ) : (
          <>
            {invitedGroups.length > 0 && (
              <>
                <SectionHeading>Invitations</SectionHeading>
                <Panel mb={4}>
                  <Table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {invitedGroups.map((x) => (
                        <tr key={x.id}>
                          <td>
                            <Link to={`/solutions/my-groups/${x.id}`}>
                              <strong>{x.name}</strong>
                            </Link>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Button
                              small
                              onClick={() => rejectInvitation(x)}
                              mr={1}
                            >
                              Decline
                            </Button>
                            <Button
                              small
                              variant="primary"
                              onClick={() => acceptInvitation(x)}
                            >
                              Accept
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Panel>
              </>
            )}
            <SectionHeading>Your groups</SectionHeading>
            {activeGroups.length > 0 ? (
              <Panel>
                <Table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th style={{ textAlign: "right" }}>Your role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeGroups.map((x) => (
                      <tr key={x.id}>
                        <td>
                          <Link to={`/solutions/my-groups/${x.id}`}>
                            <strong>{x.name}</strong>
                          </Link>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Label size="small">
                            {levelNames[x.membershipLevel]}
                          </Label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Panel>
            ) : (
              <Panel>
                <EmptyState icon={FaUsers}>
                  You're not in any groups yet. When someone adds you to a
                  group, it will show here.
                </EmptyState>
              </Panel>
            )}
          </>
        )}
      </CenterColumn>
    </>
  );
};

export default MyGroups;
